import { Flex, HStack, Link, Select, Spinner } from "@chakra-ui/react";
import CDS from "design-system";
import { FiExternalLink } from "react-icons/fi";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "helpers/url";
import { fetchCustomForms } from "features/Resources/CustomForm/api";
import { customFormKeys } from "features/Resources/CustomForm/hooks";
import {
  TemplateTaskActionButton,
  PrebuiltTemplateTaskActionButton,
  PrebuitTaskTemplateActionButtonParentProps,
  TaskTemplateActionButtonParentProps,
  TemplateTaskActionCloseButton,
} from "./TemplateTaskActionButton";
import TemplateActionText from "../TemplateActionText";
import { TaskTemplate } from "../../models";

interface Props extends TaskTemplateActionButtonParentProps {
  form: UseFormReturn<TaskTemplate>;
}

export default function TemplateSelectCustomFormTaskAction({ autoOpen, setAutoOpen, onRemove, form }: Props) {
  useEffect(() => {
    if (autoOpen) setAutoOpen(false);
  }, [autoOpen, setAutoOpen]);

  const { setValue, watch } = form;
  const currentId = watch("customFormId");
  const currentIdStr = currentId == null ? "" : String(currentId);

  const { data, isLoading } = useQuery({
    queryKey: [...customFormKeys.lists()],
    queryFn: ({ signal }) => fetchCustomForms("", signal),
    staleTime: 1000 * 60 * 3,
  });
  const forms = data?.data;

  // String coercion because Rails serializes primary keys as strings but the
  // yup schema types customFormId as number — mixed representations show up
  // depending on whether the value came from user selection or an API load.
  const selected = forms?.find((f) => String(f.id) === currentIdStr);

  return (
    <TemplateTaskActionButton
      autoOpen={autoOpen}
      onRemove={onRemove}
      buttonText={selected ? `Form: ${selected.name}` : "Choose a Form"}
      buttonTitle="Complete Form"
    >
      <Flex direction="column" pb="4" gap="4" w="100%">
        <TemplateActionText text="Which form should the assignee fill out?" />
        {isLoading ? (
          <Spinner />
        ) : forms && forms.length > 0 ? (
          <>
            <Select
              placeholder="Select a form"
              value={currentIdStr}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : null;
                setValue("customFormId", value, { shouldDirty: true });
              }}
            >
              {forms.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {f.name}
                </option>
              ))}
            </Select>
            {selected && (
              <HStack spacing={1}>
                <Link
                  href={generatePath({ path: PEOPLE_TEAM_ROUTES.customForms.show, params: { id: String(selected.id) } })}
                  isExternal
                  color="blue.600"
                  fontSize="sm"
                >
                  Edit &ldquo;{selected.name}&rdquo;
                </Link>
                <FiExternalLink size={12} color="var(--chakra-colors-blue-600)" />
              </HStack>
            )}
          </>
        ) : (
          <CDS.Text14 color="fg.subtle">No forms exist yet. Create one under Templates → Form.</CDS.Text14>
        )}
      </Flex>
      <TemplateTaskActionCloseButton />
    </TemplateTaskActionButton>
  );
}

export function PrebuiltTemplateSelectCustomFormTaskAction({ prebuiltTaskTemplate }: PrebuitTaskTemplateActionButtonParentProps) {
  return (
    <PrebuiltTemplateTaskActionButton buttonText="Complete Form" buttonTitle="Complete Form">
      <Flex direction="column" pb="4" gap="4" w="100%">
        <TemplateActionText text={`Form ID: ${prebuiltTaskTemplate.customFormId ?? "—"}`} />
      </Flex>
    </PrebuiltTemplateTaskActionButton>
  );
}
